import { SignalrService } from '@services/signalr/signalr.service';
import { NotificationHubService } from '@services/signalr/notificationHub.service';
import { Subscription } from 'rxjs';
import {Component, OnInit, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy, OnDestroy} from '@angular/core';
import { SessionService } from '@services/session/session.service';
import { NotificationCenterService } from '@services/notificationCenter/notification-center.service';
import { NotificationItem } from '@serviceModels/NotificationCenter.models';
import { environment } from '@environments/environment';
import _ from 'lodash';
import {GrowlService,GrowlSeverity} from '@common_libraries/controls/Growl/services/growl.service';
import {PollGuard} from '@library/helpers/PollGuard';
import {NotificationCenterHelperService} from '@services/notificationCenter/helpers/notification-center-helper.service';
import { TooltipModule } from 'primeng/tooltip';


@Component({
    selector: 'app-notification-center',
    templateUrl: './notification-center.component.html',
    styles: [],
    standalone: true,
    imports: [TooltipModule]
})
export class NotificationCenterComponent implements OnInit, OnChanges, OnDestroy {
  
  @Input() stamp: number = 0;
  notifyCount: number;
  allNotifications: NotificationItem[] = [];
  downloadNotifications: NotificationItem[] = [];
  pageLoading: boolean = true;
  showNotification: boolean = false;
  pollGuardSubscription: Subscription;
  lastModifiedDate: any = null;
  obSubs: any = [];

  cypressDownloadAttr: string = null;

  constructor(private sessionSvc: SessionService, private notifySvc: NotificationCenterService,
              private growlService: GrowlService, private ncHelper: NotificationCenterHelperService,
              private SignalRService: SignalrService,
              private notificationHub: NotificationHubService) {}


  ngOnInit() {
    setTimeout(()=> {
      this.ncHelper.refreshNCSubject(this);
    }, 3000);

    this.pollGuardSubscription = new Subscription();
    this.notifyCount = 0;
    if (!window.location.href.includes('/login') && this.sessionSvc.GetIsLoggedIn() === true) {
      this.getNotifications();
      this.setupNotifyRefresher();
    }

    this.obSubs.push(this.ncHelper.ncReceiverObservable.subscribe((p) => {
      this.ncHelper.refreshNCSubject(this);
    }));
  }

  ngOnDestroy(): void {
    this.ncHelper.refreshNCSubject(null);
    this.obSubs.forEach((sub) => {
      sub.unsubscribe();
    });
  }

  public setupNotifyRefresher() {
    this.SignalRService.signalrConnection$.subscribe(connection => { 
      this.notificationHub.notificationCenterUpdateListener().pipe().subscribe(
        (data: any) => {
          if (data.refresh) {
            this.getNotifications();
          }
        },
        (error: any) => {}

      );
    });
  }

  updateNotificationCenterFromPoll(){
    this.getNotifications();
  }

  ngOnChanges(changes: SimpleChanges) {    
    if (changes.stamp.previousValue !== undefined) {    

    }
  }

  showNotificationCenterClick() {
    this.showNotification = !this.showNotification;
    if (this.showNotification) {
      this.pageLoading = true; 
      this.getNotifications();
    }
  }

  getNotifyCount() {    
    this.notifySvc.GetNotificationUnReadCount().subscribe(x => {      
      this.notifyCount = x;

    });
  }

  getNotifications() {
    this.notifySvc.GetNotifications().subscribe(x => {
      // store every notification for processing
      this.allNotifications = x;

      // anything not read or not deleted
      const unread = this.allNotifications.filter(t => t.dateRead == null && t.isDeleted === false);
      this.notifyCount = unread.length;
      unread.forEach((t) => {
        if(t.showContextualNotification){
          this.growlService.notify(GrowlSeverity.warn, t.displayName, t.details);
        }
      });
      this.buildNotificationDataSets();
      
      this.resortNotifications();

      this.pageLoading = false; 
    }, () => {
      this.pageLoading = false; 
    });
  }

  getUnShownNotifications() {
    this.notifySvc.GetUnShownNotifications().subscribe(x => {

      x.forEach((t) => {
        if(t.showContextualNotification){
          this.growlService.notify(GrowlSeverity.warn, t.displayName, t.details);
        }
      });

    });
  }

  buildNotificationDataSets() {
    // build download notifications data set
    this.downloadNotifications = this.allNotifications.filter(t => t.notificationTypeID === 1);

    this.resortNotifications();
  }

  redirect(item: NotificationItem) {
    // anytime they click it, mark it as read
    this.markAsRead(item);

    if (item.redirectURL != null && item.redirectURL.length > 0) {
      // file download
      if (item.notificationTypeID === 1) {
        if (window.Cypress) {
          this.cypressDownloadAttr = item.redirectURL;
        } else {
          window.location.href = environment.webApiUrl + '/' + item.redirectURL;
        }
      }
    }
    this.markAsRead(item);
  }

  markAsRead(item: NotificationItem) {
    // mark specific item as read
    item.dateRead = new Date();
    item.isRead = true;  

    this.resortNotifications();

    // anything not read or deleted
    this.notifyCount = this.allNotifications.filter(t => t.dateRead == null && t.isDeleted === false).length;
    this.notifySvc.MarkAsRead(item.notifyID).subscribe(x => { });
  }

  deleteItem(item: NotificationItem) {
    // mark specific item as deleted
    item.isDeleted = true;
        
    // remove deleted item from notifications
    this.allNotifications = this.allNotifications.filter(x => x.notifyID !== item.notifyID);

    // rebuild all datasets
    this.buildNotificationDataSets();

    // get new notification count
    this.notifyCount = this.allNotifications.filter(t => t.dateRead == null && t.isDeleted === false).length;

    this.notifySvc.Delete(item.notifyID).subscribe(x => {   });
  }

  markAllAsRead(notificationTypeID: number) {
    // mark all notifications of this type as read
    this.allNotifications
    .filter(t => t.notificationTypeID === notificationTypeID)
    .map(m => {
      m.dateRead = new Date();
      m.isRead = true;
    });

    // rebuild all datasets
    this.buildNotificationDataSets();

    // get new count that is anything that isnt deleted or read
    this.notifyCount = this.allNotifications.filter(t => t.dateRead == null && t.isDeleted === false).length;

    this.notifySvc.MarkAllAsRead(notificationTypeID).subscribe((res: any) => {});
  }

  deleteAll(notificationTypeID: number) {
    // mark all notifications of this type as is deleted
    this.allNotifications
    .filter(f => f.notificationTypeID === notificationTypeID)
    .map(m => {
      m.isDeleted = true;
    });

    // get new count that is anything that isnt deleted or read
    this.notifyCount = this.allNotifications.filter(t => t.dateRead == null && t.isDeleted === false).length;

    // set all notifications to remove all of the deleted notification type id
    this.allNotifications = this.allNotifications.filter(f => f.notificationTypeID !== notificationTypeID);

    // rebuild all datasets
    this.buildNotificationDataSets();

    this.notifySvc.DeleteAll(notificationTypeID).subscribe((res: any) => {});
  }

  resortNotifications() {
    this.downloadNotifications = _.orderBy(this.downloadNotifications, ['isRead', 'notifyID'], ['asc', 'desc']);
  }
}
